import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { Contractor } from "@/api/williams/contractors";
import { ExpandableCard, PinButton } from "../../components";
import { faChevronDown, faChevronUp, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { getStatusText } from "../lookups/status";
import { highlight } from "@/util";
import { Link } from "react-router-dom";
import { Project } from "@/api/project";
import { ScopeOfWorkFilters, ScopeOfWorkListView } from "@/api/williams/scopesOfWork";
import { String } from "@/util";
import { useDeleteScope, useDuplicateScope, usePinScope, useScopeOfWorkList } from "../hooks";
import { useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";
import { useState } from "react";

export type ScopesOfWorkProps = {
  filters: Partial<ScopeOfWorkFilters>;
  project?: Project;
  contractor?: Contractor;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function ScopesOfWork({ filters, project, contractor, ...props }: ScopesOfWorkProps) {
  const scopes = useScopeOfWorkList(filters);
  const permissions = usePermissions();

  const data = scopes.data?.pages.flatMap((page) => page.results) ?? [];
  const total = scopes.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Scopes of Work (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.scopeOfWork.create)} state={{ project, contractor }} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Add Scope of Work" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={scopes.fetchNextPage}
        hasMore={Boolean(scopes.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {scopes.isLoading ? (
          <p>Loading...</p>
        ) : scopes.isError ? (
          <p>An unknown error occurred.</p>
        ) : scopes.isSuccess && data.length > 0 ? (
          <ScopeOfWorkTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No scopes of work found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

function ScopeOfWorkTable({ data, search }: { data: ScopeOfWorkListView[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-2">SOW ID</th>
          <th className="col-5 d-none d-md-table-cell">Project</th>
          <th className="col-5 d-none d-md-table-cell">Description</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((scope) => (
          <ScopeOfWorkRow key={scope.id} scope={scope} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function ScopeOfWorkRow({ scope, search }: { scope: ScopeOfWorkListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinScope();
  const permissions = usePermissions();

  return (
    <>
      <tr key={`${scope.id}_${scope.pinned}`}>
        <td>
          <Link to={getFullPath(routes.scopeOfWork.open(scope.id))} className="link link-primary">
            {highlight(scope.number, search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">
          <Link to={getFullPath(routes.project.open(scope.projectId))} className="link link-primary">
            {highlight(String.truncate(scope.projectNumber, 50), search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(scope.description, 50), search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={scope.pinned} onChange={() => pin.mutate(scope.id)} />
          {permissions.data?.accessLevel !== AccessLevel.GeneralUser && (
            <>
              <FontAwesomeIcon
                type="button"
                icon={faEllipsisVertical}
                size="sm"
                className="dropdown-toggle"
                title="More Options"
                data-bs-toggle="dropdown"
                fixedWidth={true}
              />
              <DropdownMenu scope={scope} />
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails scope={scope} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails scope={scope} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ scope, search }: { scope: ScopeOfWorkListView; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Scope of Work Details</div>
      <div className="p-1">
        <dl>
          <dt className="d-block d-md-none">Project</dt>
          <dd className="d-block d-md-none">
            <Link to={getFullPath(routes.project.open(scope.projectId))} className="link link-primary">
              {highlight(scope.projectNumber, search)}
            </Link>
          </dd>
          <dt>Status</dt>
          <dd>{getStatusText(scope.status)}</dd>
          <dt>Description</dt>
          <dd>{highlight(scope.description, search)}</dd>
          <dt>Owner</dt>
          <dd>{scope.owner}</dd>
          <dt>Welding/NDE Specialist</dt>
          <dd>{scope.weldingSpecialist}</dd>
        </dl>
      </div>
    </div>
  );
}

export function DropdownMenu({ scope }: { scope: ScopeOfWorkListView }) {
  const dialog = useModalDialog();
  const deleteScope = useDeleteScope(false);
  const permissions = usePermissions();
  const duplicate = useDuplicateScope();

  const accessLevel = permissions.data?.accessLevel ?? AccessLevel.GeneralUser;

  function handleDelete() {
    dialog.show(ConfirmationDialog, {
      caption: "Delete SOW",
      message: `Are you sure you want to delete scope of work ${scope.number}?`,
      onAccept: () => {
        deleteScope.mutate(scope.id);
      },
    });
  }

  return (
    <ul className="dropdown-menu">
      {accessLevel >= AccessLevel.AIWeldingSME && (
        <li className="dropdown-item cursor-pointer" onClick={handleDelete}>
          Delete
        </li>
      )}
      <li className="dropdown-item cursor-pointer" onClick={() => duplicate(scope.id)}>
        Create Duplicate
      </li>
    </ul>
  );
}
